import { Injectable, NgZone } from "@angular/core";
import { BaseConsentService } from "@apps/patient-core/src/app/services/abstracts/consent.service";
import { CookieChangeEvent, CookieChangeType } from "@apps/patient-core/types";
import { logger } from "@dentally/ui/logger";

enum E_CookieGroup {
  /**
   * Essential cookies.
   *  * Required for the website to function
   *  * Examples: session management, authentication, site preferences
   */
  ESSENTIAL = "C0001",
  /**
   * Performance / Analytics cookies
   *  * Used for analytics, performance monitoring, and user behavior tracking
   *  * Examples: Google Analytics, New Relic, Mixpanel
   */
  PERFORMANCE = "C0002",
  /**
   * Functional cookies
   *  * Enable additional functionality / personalization
   *  * Examples: chat widgets, embedded videos
   *
   * Not used or set by OneTrust currently
   */
  FUNCTIONAL = "C0003",
  /**
   * Targeting / Advertising cookies
   *  * Used for marketing and targeted advertising
   *  * Examples: re-marketing, social media ads, etc.
   */
  TARGETING = "C0004",
}
type EnabledCookieGroupsList = Array<E_CookieGroup>;

function parseCookie(cookieString: string): EnabledCookieGroupsList {
  return (decodeURIComponent(cookieString || "").match(/C[0-9]+:1/g) || []) // Find all the groups that are enabled
    .map((match) => match.split(":")[0] as E_CookieGroup) as EnabledCookieGroupsList;
}

/**
 * This service tracks user consent via the OneTrust 'OneTrustGroupsUpdated' event.
 */
@Injectable({
  providedIn: "root",
})
export class PatientConsentService extends BaseConsentService {
  constructor(private _zone: NgZone) {
    super();

    if (!window.cookieStore) {
      logger.warn("cookieStore not supported", "bfafe67b");
      return;
    }

    this._zone.runOutsideAngular(() => {
      try {
        window.cookieStore.addEventListener("change", (event: CookieChangeEvent) => {
          this._zone.run(() => {
            event.changed.forEach((change: CookieChangeType) => {
              if (change.name === "OptanonConsent") {
                const groupsString = parseCookie(change.value);
                this._handleGroups(groupsString);
              }
            });
          });
        });
      } catch (error) {
        logger.error("Error adding cookieStore listener", "87fff101", error);
      }
    });
  }

  private _handleGroups(groups: EnabledCookieGroupsList): void {
    this._isPerformanceAllowed.set(groups.includes(E_CookieGroup.PERFORMANCE));
    this._isTargetingAllowed.set(groups.includes(E_CookieGroup.TARGETING));
  }
}
